import React, { useState, useEffect } from 'react';
import './StatsManager.css';

function StatsManager() {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStat, setEditingStat] = useState(null);
  const [message, setMessage] = useState('');

  const iconOptions = [
    { value: '👥', label: 'People (👥)' },
    { value: '💼', label: 'Briefcase (💼)' },
    { value: '✅', label: 'Checkmark (✅)' },
    { value: '⭐', label: 'Star (⭐)' },
    { value: '🚀', label: 'Rocket (🚀)' },
    { value: '💰', label: 'Money (💰)' },
    { value: '🎯', label: 'Target (🎯)' },
    { value: '📈', label: 'Chart (📈)' },
    { value: '🏆', label: 'Trophy (🏆)' },
    { value: '💡', label: 'Lightbulb (💡)' },
  ];

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      // Ensure data is always an array
      setStats(Array.isArray(data) ? data : []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats([]);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const formData = new FormData(e.target);
    
    const statData = {
      icon: formData.get('icon'),
      value: parseInt(formData.get('value')),
      label: formData.get('label'),
      description: formData.get('description'),
      order: editingStat ? editingStat.order : stats.length
    };

    try {
      const url = editingStat 
        ? `/api/stats/update/${editingStat.id}`
        : '/api/stats/create';
      
      const response = await fetch(url, {
        method: editingStat ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(statData)
      });

      if (response.ok) {
        setMessage(editingStat ? 'Στατιστικό ενημερώθηκε!' : 'Στατιστικό δημιουργήθηκε!');
        setEditingStat(null);
        fetchStats();
        e.target.reset();
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error saving stat:', error);
      setMessage('Σφάλμα κατά την αποθήκευση');
    }
  };

  const handleEdit = (stat) => {
    setEditingStat(stat);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το στατιστικό;')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/stats/delete/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setMessage('Στατιστικό διαγράφηκε!');
        fetchStats();
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error deleting stat:', error);
      setMessage('Σφάλμα κατά τη διαγραφή');
    }
  };

  const handleReorder = async (id, direction) => {
    const index = stats.findIndex(s => s.id === id);
    if ((direction === 'up' && index === 0) || 
        (direction === 'down' && index === stats.length - 1)) {
      return;
    }

    const newStats = [...stats];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newStats[index], newStats[targetIndex]] = [newStats[targetIndex], newStats[index]];

    const token = localStorage.getItem('token');
    try {
      await fetch('/api/stats/reorder', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ stats: newStats.map((s, i) => ({ id: s.id, order: i })) })
      });
      fetchStats();
    } catch (error) {
      console.error('Error reordering:', error);
    }
  };

  if (loading) {
    return <div className="loading">Φόρτωση...</div>;
  }

  return (
    <div className="stats-manager">
      <h2>Διαχείριση Στατιστικών</h2>
      
      {message && <div className="message success">{message}</div>}

      <div className="stats-form-container">
        <h3>{editingStat ? 'Επεξεργασία Στατιστικού' : 'Προσθήκη Νέου Στατιστικού'}</h3>
        <form onSubmit={handleSubmit} className="stat-form">
          <div className="form-group">
            <label>Εικονίδιο</label>
            <select 
              name="icon" 
              defaultValue={editingStat?.icon}
              required
            >
              <option value="">Επιλέξτε εικονίδιο</option>
              {iconOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Αριθμός</label>
            <input 
              type="number" 
              name="value" 
              defaultValue={editingStat?.value}
              placeholder="π.χ. 250"
              required 
            />
          </div>

          <div className="form-group">
            <label>Ετικέτα</label>
            <input 
              type="text" 
              name="label" 
              defaultValue={editingStat?.label}
              placeholder="π.χ. Ενεργοί Freelancers"
              required 
            />
          </div>

          <div className="form-group">
            <label>Περιγραφή (προαιρετικό)</label>
            <textarea 
              name="description" 
              defaultValue={editingStat?.description}
              placeholder="Σύντομη περιγραφή..."
              rows="2"
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary">
              {editingStat ? 'Ενημέρωση' : 'Προσθήκη'}
            </button>
            {editingStat && (
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setEditingStat(null)}
              >
                Ακύρωση
              </button>
            )}
          </div>
        </form>

        <div className="suggestions">
          <h4>💡 Προτεινόμενα Στατιστικά για να Προσελκύσετε Χρήστες:</h4>
          <ul>
            <li><strong>Αριθμός Freelancers:</strong> Δείχνει το μέγεθος της κοινότητας</li>
            <li><strong>Ολοκληρωμένα Projects:</strong> Αποδεικνύει την εμπιστοσύνη και την επιτυχία</li>
            <li><strong>Ποσοστό Ικανοποίησης:</strong> Κτίζει εμπιστοσύνη (π.χ. 98%)</li>
            <li><strong>Συνολική Αξία Έργων:</strong> Δείχνει την οικονομική αξία της πλατφόρμας</li>
            <li><strong>Μέσος Χρόνος Ανταπόκρισης:</strong> Τονίζει την ταχύτητα (π.χ. 2 ώρες)</li>
            <li><strong>Κατηγορίες Υπηρεσιών:</strong> Δείχνει την ποικιλία</li>
          </ul>
        </div>
      </div>

      <div className="stats-list">
        <h3>Υπάρχοντα Στατιστικά</h3>
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <div key={stat.id} className="stat-card">
              <div className="stat-preview">
                <div className="stat-icon-preview">{stat.icon}</div>
                <div className="stat-value-preview">{stat.value.toLocaleString()}</div>
                <div className="stat-label-preview">{stat.label}</div>
                {stat.description && (
                  <div className="stat-desc-preview">{stat.description}</div>
                )}
              </div>
              <div className="stat-actions">
                <button 
                  onClick={() => handleReorder(stat.id, 'up')}
                  disabled={index === 0}
                  className="btn-icon"
                  title="Μετακίνηση πάνω"
                >
                  ⬆️
                </button>
                <button 
                  onClick={() => handleReorder(stat.id, 'down')}
                  disabled={index === stats.length - 1}
                  className="btn-icon"
                  title="Μετακίνηση κάτω"
                >
                  ⬇️
                </button>
                <button 
                  onClick={() => handleEdit(stat)}
                  className="btn-icon"
                  title="Επεξεργασία"
                >
                  ✏️
                </button>
                <button 
                  onClick={() => handleDelete(stat.id)}
                  className="btn-icon delete"
                  title="Διαγραφή"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default StatsManager;
